import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Download,
  Edit3,
  Loader2,
  RefreshCw,
  Sparkles,
  X,
  Wand2,
  AlertCircle,
  MoreHorizontal,
  Trash2,
  Check,
  Share2,
  Lock,
  Copy,
  Inbox,
  Phone,
  Mail,
  MapPin,
  ImagePlus,
  Upload,
  ListChecks,
  Star,
  Plus,
  Shuffle,
} from 'lucide-react';
import Navbar from '../components/site/Navbar';
import { Footer } from '../components/site/FooterCTA';
import GuidedIntake from '../components/site/GuidedIntake';
import StatsPanel from '../components/site/StatsPanel';
import GetFoundOnGoogle from '../components/site/GetFoundOnGoogle';
import { useAuth } from '../lib/AuthContext';
import { supabase } from '../lib/supabase';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 4 * 60 * 1000; // 4 minutes — matches backend's 200s LLM timeout with headroom

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return ''; }
};

const ProjectDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  // Depend on `userId` (a stable string) instead of the whole `user`
  // object anywhere we're keying effects. Supabase-js hands us a fresh
  // `session` object on every token refresh, which means a fresh `user`
  // reference — using `user` in a useEffect dep array would re-fire the
  // effect on every refresh, and if that effect kicks off any fetch that
  // itself calls `getSession()`, you get the mount → fetch → refresh →
  // remount → fetch → refresh loop.
  const userId = user?.id || null;

  const [project, setProject] = useState(null);
  const [loadState, setLoadState] = useState('loading');
  const [error, setError] = useState('');

  const [genStatus, setGenStatus] = useState('idle');
  const [genError, setGenError] = useState('');
  const [genLimitReason, setGenLimitReason] = useState(''); // '' | 'user_daily_limit' | 'global_daily_limit'
  // BUG FIX: pollStartTs must be a ref, not React state.
  // Previously it was `useState(0)`, and `pollOnce` captured its value via useCallback closure.
  // `startPolling` scheduled the first setTimeout with a pollOnce that had `pollStartTs = 0` in
  // its closure (React hadn't re-rendered yet after `setPollStartTs(Date.now())`). Three seconds
  // later, the timeout branch computed `Date.now() - 0 ≈ 1.8e12 ms`, which is >>> the 240s cap,
  // so `setGenStatus('timeout')` fired and the false Retry button appeared, even though the
  // background generation was still happily running and would succeed. Using a ref makes the
  // current start-timestamp always visible to pollOnce without relying on closure freshness.
  const pollStartTsRef = useRef(0);
  const pollTimerRef = useRef(null);
  const pollingRef = useRef(false);

  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  // Wizard on Regen: saved intake answers (if any) + which editor to show.
  // Default is the wizard when the project has a saved intake, and the raw
  // textarea for older projects that predate the intake feature.
  const [savedIntake, setSavedIntake] = useState(null);
  const [intakeLoaded, setIntakeLoaded] = useState(false);
  const [editMode, setEditMode] = useState('wizard'); // 'wizard' | 'raw'
  const [wizardBusy, setWizardBusy] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [sharing, setSharing] = useState(false);
  const [copyStatus, setCopyStatus] = useState('idle'); // idle | copied
  const [upgradePrompt, setUpgradePrompt] = useState(null); // null | string message when 403 upgrade_required
  const [pendingPlaceholders, setPendingPlaceholders] = useState(null); // null | {message, placeholders[]}
  const [confirmPublish, setConfirmPublish] = useState(false); // soft-confirm modal shown when publishing with unfilled placeholders

  // "Fill missing fields" side panel (Publish Checklist companion)
  const [fillOpen, setFillOpen] = useState(false);
  const [fillValues, setFillValues] = useState({}); // { "[Add your ABN]": "12 345..." }
  const [fillSaving, setFillSaving] = useState(false);
  const [fillError, setFillError] = useState('');

  // Live scan of the current generated_html for [Add your …] placeholders.
  // This drives the badge next to Publish and the checklist item list.
  const placeholders = useMemo(() => {
    const html = project?.generated_html || '';
    if (!html) return [];
    const re = /\[Add[^\]]{1,80}\]/gi;
    const seen = new Map();
    for (const m of html.matchAll(re)) {
      const key = m[0].trim();
      if (!seen.has(key.toLowerCase())) seen.set(key.toLowerCase(), key);
      if (seen.size >= 8) break;
    }
    return [...seen.values()];
  }, [project?.generated_html]);
  const placeholderCount = placeholders.length;

  // Editable defaults for the "Fill missing fields" panel. Only NEUTRAL
  // operational defaults live here (hours, response window). Anything that
  // is a CLAIM about the business — Licensed & insured, ABN, licence number,
  // years in business, ratings — is deliberately left blank so the owner
  // actively types what's true. Guardrail against reintroducing fabricated
  // facts through a preset.
  const placeholderDefault = useCallback((ph) => {
    const s = String(ph || '').toLowerCase();
    if (/business\s*hours|opening\s*hours|hours\b/.test(s)) return 'Mon-Fri 7:00am-5:00pm';
    if (/response\s*time|call\s*back/.test(s)) return 'We respond within 1 hour';
    return '';
  }, []);

  // Pre-populate default values whenever the panel opens or the placeholder
  // list changes, without clobbering anything the owner has already typed.
  useEffect(() => {
    if (!fillOpen) return;
    setFillValues((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const ph of placeholders) {
        if (next[ph] == null || next[ph] === '') {
          const def = placeholderDefault(ph);
          if (def) { next[ph] = def; changed = true; }
        }
      }
      return changed ? next : prev;
    });
  }, [fillOpen, placeholders, placeholderDefault]);

  // Leads (Part 3): submissions from the generated page's quote form.
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsError, setLeadsError] = useState('');

  // Owner photos (Part: "Add your photos")
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [photosError, setPhotosError] = useState('');
  const [uploading, setUploading] = useState(false);
  const photoInputRef = useRef(null);

  // Owner-typed reviews (testimonials). Owner-only; never generated.
  const [reviews, setReviews] = useState([]);
  const [reviewsError, setReviewsError] = useState('');
  const [reviewDraft, setReviewDraft] = useState(null); // null | {id?, review_text, first_name, suburb, rating}
  const [reviewSaving, setReviewSaving] = useState(false);

  // The shareable URL points at the FastAPI-served raw HTML variant
  // (`/api/public/projects/{id}/index.html`) instead of the SPA route
  // `/p/{id}`, because Emergent's Cloudflare edge intercepts non-`/api`
  // paths and returns the generic React shell — so social scrapers
  // (Facebook, WhatsApp, LinkedIn, Twitter) never see business-specific
  // OG tags on `/p/{id}` in production. The `/api/...` URL reaches
  // FastAPI directly, which already returns the full landing page with
  // <title>, meta description, og:*, twitter:*, canonical, and the
  // LocalBusiness JSON-LD in the initial response.
  //
  // The owner's in-app preview (opened via the "Preview" tab) still
  // uses `/p/{id}` — this only changes what gets copied / opened when
  // the owner clicks "Share".
  //
  // Memoised on `id` + `slug` so downstream consumers get a stable
  // reference — any effect that ever keys on `shareUrl` won't re-fire
  // per render. Uses the owner-picked slug variant when set:
  //   /api/public/{slug}
  const [slug, setSlug] = useState(null);
  const [slugAuto, setSlugAuto] = useState(true);
  const shareUrl = useMemo(
    () => {
      if (typeof window === 'undefined') return '';
      const base = window.location.origin;
      return slug
        ? `${base}/api/public/${slug}`
        : `${base}/api/public/projects/${id}/index.html`;
    },
    [id, slug],
  );

  const autoRunRef = useRef(false);
  const autoTrigger = !!location.state?.autoGenerate;

  const getToken = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  }, []);

  // Force a fresh Stripe entitlement check. Fire-and-forget. Used on mount so
  // a user returning from Stripe checkout sees their newly-started trial reflect
  // immediately (no webhook needed).
  const refreshSubscription = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) return null;
      const { data } = await axios.post(
        `${API}/subscription/refresh`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 }
      );
      return data;
    } catch {
      return null;
    }
  }, [getToken]);

  const loadLeads = useCallback(async () => {
    setLeadsLoading(true);
    setLeadsError('');
    try {
      const token = await getToken();
      if (!token) return;
      const { data } = await axios.get(`${API}/projects/${id}/leads`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 15000,
      });
      setLeads(Array.isArray(data) ? data : []);
    } catch (err) {
      // Silent unless it's a real error (404 = project not there yet is fine).
      if (err?.response?.status && err.response.status !== 404) {
        setLeadsError(err?.response?.data?.detail || err?.message || 'Could not load leads.');
      }
      setLeads([]);
    } finally {
      setLeadsLoading(false);
    }
  }, [id, getToken]);

  const loadPhotos = useCallback(async () => {
    setPhotosLoading(true);
    setPhotosError('');
    try {
      const token = await getToken();
      if (!token) return;
      const { data } = await axios.get(`${API}/projects/${id}/photos`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 15000,
      });
      setPhotos(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status && err.response.status !== 404) {
        setPhotosError(err?.response?.data?.detail || err?.message || 'Could not load photos.');
      }
      setPhotos([]);
    } finally {
      setPhotosLoading(false);
    }
  }, [id, getToken]);

  const uploadPhotos = useCallback(async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setPhotosError('');
    const files = Array.from(fileList);
    const remaining = 8 - photos.length;
    if (remaining <= 0) {
      setPhotosError('You already have 8 photos on this project. Remove one first.');
      return;
    }
    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) {
      setPhotosError(`Only ${remaining} more photo${remaining === 1 ? '' : 's'} allowed. The rest were skipped.`);
    }
    setUploading(true);
    try {
      const token = await getToken();
      if (!token) throw new Error('You need to be logged in.');
      const uploaded = [];
      for (const file of toUpload) {
        if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
          setPhotosError(`"${file.name}" is not a supported format (JPG, PNG, WebP).`);
          continue;
        }
        if (file.size > 5 * 1024 * 1024) {
          setPhotosError(`"${file.name}" is larger than 5MB.`);
          continue;
        }
        const form = new FormData();
        form.append('file', file);
        form.append('alt', file.name.replace(/\.[^.]+$/, '').slice(0, 80));
        try {
          const { data } = await axios.post(
            `${API}/projects/${id}/photos/upload`,
            form,
            { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 },
          );
          uploaded.push(data);
        } catch (err) {
          const detail = err?.response?.data?.detail;
          setPhotosError(typeof detail === 'string' ? detail
            : (detail?.message || err?.message || `Could not upload ${file.name}.`));
        }
      }
      if (uploaded.length) {
        setPhotos((prev) => [...prev, ...uploaded]);
      }
    } finally {
      setUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  }, [id, getToken, photos.length]);

  const removePhoto = useCallback(async (photoId) => {
    setPhotosError('');
    try {
      const token = await getToken();
      if (!token) throw new Error('You need to be logged in.');
      await axios.delete(`${API}/projects/${id}/photos/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 15000,
      });
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setPhotosError(typeof detail === 'string' ? detail
        : (detail?.message || err?.message || 'Could not remove that photo.'));
    }
  }, [id, getToken]);

  const saveFillValues = useCallback(async () => {
    setFillError('');
    const fills = {};
    for (const [k, v] of Object.entries(fillValues || {})) {
      const val = (v || '').trim();
      if (val) fills[k] = val;
    }
    if (Object.keys(fills).length === 0) {
      setFillError('Type at least one value before saving.');
      return;
    }
    setFillSaving(true);
    try {
      const token = await getToken();
      if (!token) throw new Error('You need to be logged in.');
      const { data } = await axios.post(
        `${API}/projects/${id}/fill-placeholders`,
        { fills },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 },
      );
      if (data?.generated_html) {
        setProject((p) => ({ ...(p || {}), generated_html: data.generated_html }));
      }
      // Drop the just-saved keys from local state
      setFillValues((prev) => {
        const next = { ...prev };
        for (const k of data?.applied || []) delete next[k];
        return next;
      });
      if ((data?.remaining || []).length === 0) {
        setFillOpen(false);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setFillError(typeof detail === 'string' ? detail
        : (detail?.message || err?.message || 'Could not save. Please try again.'));
    } finally {
      setFillSaving(false);
    }
  }, [id, getToken, fillValues]);


  // -------- Reviews (owner-typed) --------
  const loadReviews = useCallback(async () => {
    setReviewsError('');
    try {
      const token = await getToken();
      if (!token) return;
      const { data } = await axios.get(`${API}/projects/${id}/reviews`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 15000,
      });
      setReviews(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status && err.response.status !== 404) {
        setReviewsError(err?.response?.data?.detail || err?.message || 'Could not load reviews.');
      }
      setReviews([]);
    }
  }, [id, getToken]);

  const openNewReview = () => {
    setReviewDraft({ review_text: '', first_name: '', suburb: '', rating: 0 });
    setReviewsError('');
  };
  const openEditReview = (r) => {
    setReviewDraft({
      id: r.id,
      review_text: r.review_text || '',
      first_name: r.first_name || '',
      suburb: r.suburb || '',
      rating: r.rating || 0,
    });
    setReviewsError('');
  };

  const saveReview = useCallback(async () => {
    if (!reviewDraft) return;
    const payload = {
      review_text: (reviewDraft.review_text || '').trim(),
      first_name: (reviewDraft.first_name || '').trim(),
      suburb: (reviewDraft.suburb || '').trim() || null,
      rating: reviewDraft.rating ? Number(reviewDraft.rating) : null,
    };
    if (!payload.review_text || !payload.first_name) {
      setReviewsError('Review text and first name are required.');
      return;
    }
    setReviewSaving(true);
    setReviewsError('');
    try {
      const token = await getToken();
      if (!token) throw new Error('You need to be logged in.');
      if (reviewDraft.id) {
        const { data } = await axios.patch(
          `${API}/projects/${id}/reviews/${reviewDraft.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 },
        );
        setReviews((prev) => prev.map((r) => (r.id === data.id ? data : r)));
      } else {
        const { data } = await axios.post(
          `${API}/projects/${id}/reviews`,
          payload,
          { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 },
        );
        setReviews((prev) => [...prev, data]);
      }
      // Reload the project HTML so the preview reflects the injected card.
      try {
        const fresh = await loadFromApi();
        setProject(fresh);
      } catch { /* silent */ }
      setReviewDraft(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setReviewsError(typeof detail === 'string' ? detail
        : (detail?.message || err?.message || 'Could not save the review.'));
    } finally {
      setReviewSaving(false);
    }
  }, [id, getToken, reviewDraft]);

  const removeReview = useCallback(async (reviewId) => {
    setReviewsError('');
    try {
      const token = await getToken();
      if (!token) throw new Error('You need to be logged in.');
      await axios.delete(`${API}/projects/${id}/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 15000,
      });
      setReviews((prev) => prev.filter((r) => r.id !== reviewId));
      try {
        const fresh = await loadFromApi();
        setProject(fresh);
      } catch { /* silent */ }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setReviewsError(typeof detail === 'string' ? detail
        : (detail?.message || err?.message || 'Could not remove that review.'));
    }
  }, [id, getToken]);

  // Shuffle the trade-hero Unsplash photo. In-place swap — no full regen,
  // no LLM call. Requires a trade set via the guided intake.
  const [heroShuffling, setHeroShuffling] = useState(false);
  const [heroShuffleMsg, setHeroShuffleMsg] = useState('');
  const shuffleHero = useCallback(async () => {
    setHeroShuffling(true);
    setHeroShuffleMsg('');
    try {
      const token = await getToken();
      if (!token) throw new Error('You need to be logged in.');
      const { data } = await axios.post(
        `${API}/projects/${id}/hero/shuffle`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 },
      );
      // Refresh the stored HTML so the preview reflects the swap.
      try {
        const fresh = await loadFromApi();
        setProject(fresh);
      } catch { /* silent */ }
      const photographer = data?.image?.photographer_name || '';
      setHeroShuffleMsg(photographer ? `New hero photo by ${photographer}.` : 'New hero photo applied.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setHeroShuffleMsg(typeof detail === 'string' ? detail
        : (err?.message || 'Could not shuffle the hero photo.'));
    } finally {
      setHeroShuffling(false);
    }
  }, [id, getToken]);


  const loadFromApi = useCallback(async () => {
    const token = await getToken();
    if (!token) throw new Error('You need to be logged in.');
    const res = await axios.get(`${API}/projects/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 15000,
    });
    return res.data;
  }, [id, getToken]);

  const loadInitial = useCallback(async () => {
    setLoadState('loading');
    try {
      const data = await loadFromApi();
      setProject(data);
      setGenStatus(data.status || 'idle');
      setGenError(data.error_message || '');
      setLoadState('ready');
    } catch (err) {
      if (err?.response?.status === 404) setLoadState('not_found');
      else {
        setError(err?.response?.data?.detail || err?.message || 'Could not load project.');
        setLoadState('error');
      }
    }
  }, [loadFromApi]);

  const stopPolling = useCallback(() => {
    pollingRef.current = false;
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const pollOnce = useCallback(async () => {
    if (!pollingRef.current) return;
    try {
      const data = await loadFromApi();
      setProject(data);
      setGenError(data.error_message || '');
      if (data.status === 'done') {
        setGenStatus('done');
        stopPolling();
        // Refresh leads too — a fresh page may have been published elsewhere.
        loadLeads();
        return;
      }
      if (data.status === 'error') {
        setGenStatus('error');
        stopPolling();
        return;
      }
      // Still generating (or idle) — check the hard cap using the CURRENT ref value.
      if (Date.now() - pollStartTsRef.current > POLL_TIMEOUT_MS) {
        setGenStatus('timeout');
        stopPolling();
        return;
      }
      pollTimerRef.current = setTimeout(pollOnce, POLL_INTERVAL_MS);
    } catch (err) {
      // Transient poll failure (network blip, 5xx, JWT refresh, cold-start slowness).
      // Do NOT surface this as an error state — keep polling silently until we either
      // see a real done/error status or blow past the 4-minute cap.
      if (Date.now() - pollStartTsRef.current > POLL_TIMEOUT_MS) {
        setGenStatus('timeout');
        stopPolling();
      } else {
        pollTimerRef.current = setTimeout(pollOnce, POLL_INTERVAL_MS);
      }
    }
  }, [loadFromApi, stopPolling, loadLeads]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollStartTsRef.current = Date.now();  // ref, not state → immediately visible to pollOnce
    pollingRef.current = true;
    pollTimerRef.current = setTimeout(pollOnce, POLL_INTERVAL_MS);
  }, [pollOnce, stopPolling]);

  const runGenerate = useCallback(async () => {
    setGenError('');
    setGenLimitReason('');
    setGenStatus('generating');
    try {
      const token = await getToken();
      if (!token) throw new Error('You need to be logged in.');
      await axios.post(
        `${API}/projects/${id}/generate`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        }
      );
      startPolling();
    } catch (err) {
      // Cost guardrail rejections: 429 with { reason, message }. Do not offer Retry.
      if (err?.response?.status === 429) {
        const d = err.response.data?.detail || {};
        const reason = typeof d === 'object' ? (d.reason || '') : '';
        const message = (typeof d === 'object' && d.message)
          ? d.message
          : (reason === 'global_daily_limit'
              ? 'YouBuild is at capacity for today. Please try again tomorrow.'
              : "You've reached today's limit of pages. Your limit resets tomorrow.");
        setGenLimitReason(reason || 'user_daily_limit');
        setGenError(message);
        setGenStatus('limit');
        return;
      }
      const msg = err?.response?.data?.detail || err?.message || 'Generation failed to start.';
      setGenError(typeof msg === 'string' ? msg : 'Generation failed to start.');
      setGenStatus('error');
    }
  }, [id, getToken, startPolling]);

  // Load the saved intake ONCE per project. Missing intake is not an error
  // — pre-intake projects simply fall back to the raw prompt editor.
  const loadIntake = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) return;
      const { data } = await axios.get(`${API}/projects/${id}/intake`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 12000,
      });
      const intake = data?.intake || null;
      setSavedIntake(intake);
      // Default the editor to whichever mode makes sense for this project.
      setEditMode(intake && intake.trade && intake.town ? 'wizard' : 'raw');
    } catch (_) {
      // 404 / network — treat as "no intake", raw mode.
      setSavedIntake(null);
      setEditMode('raw');
    } finally {
      setIntakeLoaded(true);
    }
  }, [id, getToken]);

  const loadSlug = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) return;
      const { data } = await axios.get(`${API}/projects/${id}/slug`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000,
      });
      setSlug(data?.slug || null);
      // Missing flag on legacy rows → treat as auto (owner never chose it).
      setSlugAuto(data?.auto_generated !== false);
    } catch (_) {
      // 404 / network — treat as "no slug set yet".
      setSlug(null);
      setSlugAuto(true);
    }
  }, [id, getToken]);

  useEffect(() => {
    if (authLoading) return;
    if (!userId) { navigate(`/login?next=/projects/${id}`, { replace: true }); return; }
    loadInitial();
    // Fire-and-forget: if the user just returned from Stripe checkout, this
    // refreshes their entitlement so publish unlocks without waiting on a webhook.
    refreshSubscription();
    // Load any prior quote-form submissions.
    loadLeads();
    // Load any owner-uploaded photos for the "Add your photos" panel.
    loadPhotos();
    // Load any owner-typed reviews.
    loadReviews();
    // Load saved Guided-Intake answers (if any) for the "Edit prompt" wizard.
    loadIntake();
    // Load the owner-picked custom slug (if any) for the share URL.
    loadSlug();
    return () => stopPolling();
    // Depend on `userId` (string) not `user` (object). Every loader
    // callback below is stable (only depends on id + getToken which is
    // itself `[]`). So this effect runs exactly once per (authLoading,
    // userId, id) combination — never on token refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, userId, id]);

  useEffect(() => {
    if (loadState !== 'ready') return;
    if (genStatus === 'generating' && !pollingRef.current) {
      startPolling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadState, genStatus]);

  useEffect(() => {
    if (!autoTrigger || autoRunRef.current) return;
    if (loadState !== 'ready' || !project) return;
    if (project.generated_html) return;
    if (genStatus === 'generating') return;
    autoRunRef.current = true;
    runGenerate();
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoTrigger, loadState, project, genStatus]);

  const savePromptEdit = async () => {
    const newPrompt = editValue.trim();
    if (!newPrompt) return;
    setSavingEdit(true);
    const { error } = await supabase
      .from('user_projects')
      .update({ prompt: newPrompt })
      .eq('id', id);
    setSavingEdit(false);
    if (error) { setError(error.message); return; }
    setProject((p) => ({ ...(p || {}), prompt: newPrompt }));
    setEditing(false);
    runGenerate();
  };

  // Load the saved intake ONCE per project. Missing intake is not an error
  // — pre-intake projects simply fall back to the raw prompt editor.
  // (definition hoisted above the mount-effect that uses it — see top of component)

  const openPromptEditor = () => {
    // Prefer the wizard when we have intake answers; fall back to raw
    // otherwise. Owner can still flip to Advanced via the escape hatch.
    setEditMode(savedIntake && savedIntake.trade && savedIntake.town ? 'wizard' : 'raw');
    setEditValue(project?.prompt || '');
    setEditing(true);
  };

  // Wizard submit → hit /generate-guided which re-assembles the prompt
  // server-side, upserts the intake, and reuses the same background pipeline
  // as the classic Regenerate button.
  const handleWizardRegenerate = useCallback(async (intake) => {
    setWizardBusy(true);
    setGenError('');
    setGenLimitReason('');
    try {
      const token = await getToken();
      if (!token) throw new Error('You need to be logged in.');
      await axios.post(
        `${API}/projects/${id}/generate-guided`,
        intake,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 },
      );
      // Optimistically remember the latest answers so a follow-up open of
      // the wizard shows what the owner just typed (no waiting on GET).
      setSavedIntake({ ...(savedIntake || {}), ...intake });
      setEditing(false);
      setGenStatus('generating');
      startPolling();
    } catch (err) {
      if (err?.response?.status === 429) {
        const d = err.response.data?.detail || {};
        const reason = typeof d === 'object' ? (d.reason || '') : '';
        const message = (typeof d === 'object' && d.message)
          ? d.message
          : "You've reached today's limit of pages.";
        setGenLimitReason(reason || 'user_daily_limit');
        setGenError(message);
        setGenStatus('limit');
      } else {
        const msg = err?.response?.data?.detail || err?.message || 'Could not start regeneration.';
        setGenError(typeof msg === 'string' ? msg : 'Could not start regeneration.');
        setGenStatus('error');
      }
    } finally {
      setWizardBusy(false);
    }
  }, [id, getToken, savedIntake, startPolling]);

  const downloadHtml = () => {
    if (!project?.generated_html) return;
    const blob = new Blob([project.generated_html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const filename = (project.name || 'youbuild-page').replace(/[^a-z0-9-]+/gi, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').toLowerCase() || 'page';
    a.href = url;
    a.download = `${filename}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const saveName = async () => {
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === project.name) { setRenaming(false); return; }
    setSavingName(true);
    const { error } = await supabase.from('user_projects').update({ name: trimmed }).eq('id', id);
    setSavingName(false);
    if (error) { setError(error.message); return; }
    setProject((p) => ({ ...(p || {}), name: trimmed }));
    setRenaming(false);
  };

  const doDelete = async () => {
    setDeleting(true);
    const { error } = await supabase.from('user_projects').delete().eq('id', id);
    setDeleting(false);
    if (error) { setError(error.message); return; }
    navigate('/projects', { replace: true });
  };

  const togglePublic = async (opts = {}) => {
    if (!project) return;
    if (!project.generated_html) {
      setError('Generate the page first, then you can share it.');
      return;
    }
    // Soft-confirm when publishing with placeholders still unfilled.
    const nextValue = !project.is_public;
    if (nextValue && placeholderCount > 0 && !opts.skipPlaceholderConfirm) {
      setConfirmPublish(true);
      return;
    }
    setSharing(true);
    setUpgradePrompt(null);
    setPendingPlaceholders(null);
    const doPublishCall = async () => {
      const token = await getToken();
      if (!token) throw new Error('You need to be logged in.');
      return axios.post(
        `${API}/projects/${id}/publish`,
        { is_public: nextValue },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 }
      );
    };
    try {
      const { data } = await doPublishCall();
      setProject((p) => ({ ...(p || {}), is_public: !!data?.is_public }));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Publish gate: 403 with code=upgrade_required. If the user just came back
      // from Stripe checkout, their subscription may not be cached yet — force a
      // fresh Stripe check and retry once silently before showing the upgrade prompt.
      if (err?.response?.status === 403 && detail?.code === 'upgrade_required') {
        const refreshed = await refreshSubscription();
        if (refreshed?.can_publish) {
          try {
            const { data } = await doPublishCall();
            setProject((p) => ({ ...(p || {}), is_public: !!data?.is_public }));
            setSharing(false);
            return;
          } catch (retryErr) {
            const rDetail = retryErr?.response?.data?.detail;
            if (retryErr?.response?.status === 403 && rDetail?.code === 'upgrade_required') {
              setUpgradePrompt(rDetail.message || 'Upgrade to publish live pages.');
            } else {
              const msg = (rDetail && (rDetail.message || rDetail)) || retryErr?.message || 'Could not update sharing.';
              setError(typeof msg === 'string' ? msg : 'Could not update sharing.');
            }
            setSharing(false);
            return;
          }
        }
        setUpgradePrompt(detail.message || 'Upgrade to publish live pages.');
      } else if (err?.response?.status === 400 && detail?.code === 'placeholders_pending') {
        // Hard Rule #2: block publish while any [Add your …] placeholder remains.
        const list = Array.isArray(detail.placeholders) ? detail.placeholders : [];
        setPendingPlaceholders({
          message: detail.message || 'Finish these fields first, then publish:',
          placeholders: list,
        });
      } else {
        const msg = (detail && (detail.message || detail)) || err?.message || 'Could not update sharing.';
        setError(typeof msg === 'string' ? msg : 'Could not update sharing.');
      }
    } finally {
      setSharing(false);
    }
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyStatus('copied');
      setTimeout(() => setCopyStatus('idle'), 1800);
    } catch {
      // ignore
    }
  };

  // ---- Custom slug editor state + handlers.
  const [slugDraft, setSlugDraft] = useState('');
  const [slugSaving, setSlugSaving] = useState(false);
  const [slugError, setSlugError] = useState('');
  const [slugStatus, setSlugStatus] = useState('idle'); // idle | saved

  // ---- Business-name editor state + handlers.
  // Owners of freeform-prompt projects can inherit a raw prompt as the
  // "business name" (e.g. `Build a website for "A & C Auto Electrics…"`),
  // which produces bad OG titles + ugly auto-slugs. Fixing this is an
  // instant, free, LLM-free text patch — the server updates the source
  // of truth and re-injects title / meta / og / JSON-LD at serve time.
  const [bizDraft, setBizDraft] = useState('');
  const [bizSaving, setBizSaving] = useState(false);
  const [bizError, setBizError] = useState('');
  const [bizStatus, setBizStatus] = useState('idle'); // idle | saved
  const [pendingReseed, setPendingReseed] = useState(null); // null | {old_name}
  const [reseeding, setReseeding] = useState(false);
  // Ref-guarded one-shot prefill: once we've pushed an initial value
  // into `bizDraft`, this effect stops firing forever — no combination
  // of savedIntake/project.name re-renders (StrictMode double-mount,
  // token refresh, poll refetch) can overwrite the user's keystrokes.
  const bizPrefillDoneRef = useRef(false);
  useEffect(() => {
    if (bizPrefillDoneRef.current) return;
    const preferred = (savedIntake?.business_name || project?.name || '').trim();
    if (!preferred) return;
    bizPrefillDoneRef.current = true;
    setBizDraft(preferred);
  }, [savedIntake?.business_name, project?.name]);

  const saveBizName = useCallback(async () => {
    const trimmed = (bizDraft || '').trim();
    if (!trimmed) {
      setBizError('Type your business name first.');
      return;
    }
    if (trimmed === (project?.name || '').trim()) {
      setBizError('That’s already your business name.');
      return;
    }
    setBizSaving(true);
    setBizError('');
    try {
      const token = await getToken();
      const { data } = await axios.patch(
        `${API}/projects/${id}/business-name`,
        { business_name: trimmed },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 },
      );
      // Reflect the new name locally — the HTML may or may not have
      // been body-swapped depending on whether we cleanly found the
      // old name; either way the server has stored the fresh name and
      // will re-inject SEO tags on the next serve.
      setProject((p) => ({
        ...(p || {}),
        name: data?.business_name || trimmed,
        generated_html: data?.generated_html || (p || {}).generated_html,
      }));
      setBizStatus('saved');
      setTimeout(() => setBizStatus('idle'), 1800);
      // Offer a slug reseed only when the current slug was auto-minted.
      if (data?.slug_is_auto_generated) {
        setPendingReseed({ old_name: data?.old_business_name || '' });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setBizError(typeof detail === 'string' ? detail
        : (detail?.message || err?.message || "Couldn't save that name."));
    } finally {
      setBizSaving(false);
    }
  }, [bizDraft, id, getToken, project]);

  const reseedSlug = useCallback(async () => {
    setReseeding(true);
    setBizError('');
    try {
      const token = await getToken();
      const { data } = await axios.post(
        `${API}/projects/${id}/slug/reseed`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 },
      );
      setSlug(data?.new_slug || null);
      setSlugAuto(true);
      setPendingReseed(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setBizError(typeof detail === 'string' ? detail
        : (detail?.message || err?.message || "Couldn't update the slug."));
    } finally {
      setReseeding(false);
    }
  }, [id, getToken]);

  const saveSlug = useCallback(async () => {
    const draft = (slugDraft || '')
      .trim()
      .toLowerCase()
      .replace(/[\s_]+/g, '-')
      .replace(/-{2,}/g, '-')
      .replace(/^-+|-+$/g, '');
    if (!draft) {
      setSlugError('Pick a slug first — e.g. joes-plumbing.');
      return;
    }
    setSlugSaving(true);
    setSlugError('');
    try {
      const token = await getToken();
      const { data } = await axios.put(
        `${API}/projects/${id}/slug`,
        { slug: draft },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 },
      );
      setSlug(data?.slug || draft);
      setSlugAuto(false);  // owner picked it → stable, won't be auto-reseeded
      setSlugDraft('');
      setSlugStatus('saved');
      setTimeout(() => setSlugStatus('idle'), 1800);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setSlugError(typeof detail === 'string' ? detail : (err?.message || "Couldn't save that slug."));
    } finally {
      setSlugSaving(false);
    }
  }, [slugDraft, id, getToken]);

  const clearSlug = useCallback(async () => {
    setSlugSaving(true);
    setSlugError('');
    try {
      const token = await getToken();
      await axios.delete(`${API}/projects/${id}/slug`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 8000,
      });
      setSlug(null);
      setSlugAuto(true);
      setSlugDraft('');
    } catch (err) {
      setSlugError(err?.message || "Couldn't clear the slug.");
    } finally {
      setSlugSaving(false);
    }
  }, [id, getToken]);

  const isBusy = genStatus === 'generating';

  return (
    <div className="min-h-screen font-sans antialiased" style={{ backgroundColor: 'var(--yb-bg)', color: 'var(--yb-text)' }}>
      <Navbar />
      <main>
        <section className="pt-28 pb-8 lg:pt-32 lg:pb-10">
          <div className="max-w-[1080px] mx-auto px-6 lg:px-10">
            <Link to="/projects" className="inline-flex items-center gap-1.5 text-[13.5px] mb-6" style={{ color: 'var(--yb-indigo)' }}>
              <ArrowLeft size={14} /> All projects
            </Link>

            {loadState === 'loading' && (
              <div className="flex items-center justify-center py-16" style={{ color: 'var(--yb-muted)' }}>
                <Loader2 className="animate-spin" />
              </div>
            )}
            {loadState === 'not_found' && (
              <div className="py-10">
                <h1 className="font-serif text-4xl" style={{ color: 'var(--yb-ink)' }}>Project not found</h1>
                <p className="mt-2" style={{ color: 'var(--yb-muted)' }}>It may have been deleted, or you don’t have access.</p>
              </div>
            )}
            {loadState === 'error' && (
              <p style={{ color: '#B4530A' }}>{error}</p>
            )}

            {loadState === 'ready' && project && (
              <>
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-[13px] uppercase tracking-widest mb-3" style={{ color: 'var(--yb-muted)' }}>Project</p>
                    {renaming ? (
                      <div className="flex items-center gap-2">
                        <input
                          autoFocus
                          value={nameValue}
                          onChange={(e) => setNameValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setRenaming(false); }}
                          className="font-serif text-4xl md:text-5xl leading-tight tracking-tight bg-transparent border-b outline-none flex-1 min-w-0"
                          style={{ borderColor: 'var(--yb-indigo)', color: 'var(--yb-ink)' }}
                        />
                        <button onClick={saveName} disabled={savingName} className="p-2 rounded-md" style={{ color: 'var(--yb-indigo)' }} aria-label="Save name">
                          {savingName ? <Loader2 size={16} className="animate-spin" /> : <Check size={18} />}
                        </button>
                        <button onClick={() => { setRenaming(false); setNameValue(project.name || ''); }} className="p-2 rounded-md" style={{ color: 'var(--yb-muted)' }} aria-label="Cancel rename">
                          <X size={18} />
                        </button>
                      </div>
                    ) : (
                      <h1 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight" style={{ color: 'var(--yb-ink)' }}>
                        {project.name}
                      </h1>
                    )}
                    <div className="mt-3 flex items-center gap-2 text-[12.5px]" style={{ color: 'var(--yb-muted)' }}>
                      <Calendar size={13} /> Updated {formatDate(project.updated_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={runGenerate}
                      disabled={isBusy}
                      className="yb-btn-primary inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13.5px] font-semibold disabled:opacity-70"
                    >
                      {isBusy ? <Loader2 size={14} className="animate-spin" /> : (project.generated_html ? <RefreshCw size={14} /> : <Wand2 size={14} />)}
                      {isBusy ? 'Building…' : (project.generated_html ? 'Regenerate' : 'Generate')}
                    </button>
                    <button
                      onClick={openPromptEditor}
                      disabled={isBusy}
                      data-testid="edit-prompt-btn"
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border text-[13px] bg-white disabled:opacity-60"
                      style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)' }}
                    >
                      <Edit3 size={14} /> Edit prompt
                    </button>
                    {project.generated_html && (
                      <button
                        onClick={downloadHtml}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border text-[13px] bg-white"
                        style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)' }}
                      >
                        <Download size={14} /> Download HTML
                      </button>
                    )}
                    {/* Try another photo — in-place Unsplash swap for the hero.
                        Only offered when we can build a trade-specific query
                        (i.e. the intake gave us a trade). Cheap: 1 API call + 1 HTML write. */}
                    {project.generated_html && savedIntake && savedIntake.trade && (
                      <button
                        onClick={shuffleHero}
                        disabled={heroShuffling || isBusy}
                        data-testid="hero-shuffle-btn"
                        title="Pick a different Unsplash photo for the hero (no regeneration)"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border text-[13px] bg-white disabled:opacity-60"
                        style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)' }}
                      >
                        {heroShuffling ? <Loader2 size={14} className="animate-spin" /> : <Shuffle size={14} />}
                        {heroShuffling ? 'Shuffling…' : 'Try another photo'}
                      </button>
                    )}
                    {project.generated_html && placeholderCount > 0 && (
                      <button
                        type="button"
                        onClick={() => setFillOpen(true)}
                        data-testid="publish-checklist-badge"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border text-[13px] font-medium"
                        style={{ borderColor: '#F59E0B', backgroundColor: '#FFFBEB', color: '#92400E' }}
                        title="Fill the [Add your …] fields still on the page"
                      >
                        <ListChecks size={14} />
                        {placeholderCount} {placeholderCount === 1 ? 'field to finish' : 'fields to finish'}
                      </button>
                    )}
                    <button
                      onClick={togglePublic}
                      disabled={sharing || !project.generated_html}
                      data-testid="publish-toggle-btn"
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border text-[13px] bg-white disabled:opacity-60"
                      style={{
                        borderColor: project.is_public ? 'var(--yb-indigo)' : 'var(--yb-border)',
                        color: project.is_public ? 'var(--yb-indigo)' : 'var(--yb-text)',
                      }}
                      title={!project.generated_html ? 'Generate the page first' : (project.is_public ? 'Make private' : 'Publish this page')}
                    >
                      {sharing ? <Loader2 size={14} className="animate-spin" /> : (project.is_public ? <Share2 size={14} /> : <Lock size={14} />)}
                      {project.is_public ? 'Public' : 'Share'}
                    </button>
                    <div className="relative">
                      <button onClick={() => setMenuOpen((m) => !m)} className="p-2 rounded-full border bg-white" style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-muted)' }} aria-label="Project actions">
                        <MoreHorizontal size={16} />
                      </button>
                      {menuOpen && (
                        <div
                          onMouseLeave={() => setMenuOpen(false)}
                          className="absolute right-0 top-11 z-10 w-44 bg-white rounded-xl border shadow-lg overflow-hidden"
                          style={{ borderColor: 'var(--yb-border)' }}
                        >
                          <button
                            onClick={() => { setMenuOpen(false); setNameValue(project.name || ''); setRenaming(true); }}
                            className="w-full text-left px-3 py-2 text-[13px] hover:bg-neutral-50 inline-flex items-center gap-2"
                            style={{ color: 'var(--yb-text)' }}
                          >
                            <Edit3 size={13} style={{ color: 'var(--yb-indigo)' }} /> Rename project
                          </button>
                          <button
                            onClick={() => { setMenuOpen(false); setConfirmDelete(true); }}
                            className="w-full text-left px-3 py-2 text-[13px] hover:bg-neutral-50 inline-flex items-center gap-2"
                            style={{ color: '#B4530A' }}
                          >
                            <Trash2 size={13} /> Delete project
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {upgradePrompt && (
                  <div
                    data-testid="upgrade-required-banner"
                    className="mt-4 p-4 rounded-xl border flex flex-col md:flex-row md:items-center justify-between gap-3"
                    style={{ borderColor: 'var(--yb-indigo)', backgroundColor: '#EEF2FF' }}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-[12px] uppercase tracking-wider mb-1" style={{ color: 'var(--yb-indigo)' }}>
                        Upgrade to publish
                      </p>
                      <p className="text-[13.5px]" style={{ color: 'var(--yb-ink)' }}>
                        {upgradePrompt}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <a
                        href="/#pricing"
                        data-testid="upgrade-see-plans-btn"
                        className="yb-btn-primary inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold"
                      >
                        See plans
                      </a>
                      <button
                        onClick={() => setUpgradePrompt(null)}
                        className="px-3 py-2 rounded-full border text-[13px] bg-white"
                        style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)' }}
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                )}


                {pendingPlaceholders && (
                  <div
                    data-testid="placeholders-pending-banner"
                    className="mt-4 p-4 rounded-xl border"
                    style={{ borderColor: '#F59E0B', backgroundColor: '#FFFBEB' }}
                  >
                    <p className="text-[12px] uppercase tracking-wider mb-1" style={{ color: '#B4530A' }}>
                      Finish these fields before publishing
                    </p>
                    <p className="text-[13.5px]" style={{ color: 'var(--yb-ink)' }}>
                      {pendingPlaceholders.message}
                    </p>
                    {pendingPlaceholders.placeholders.length > 0 && (
                      <ul className="mt-2 space-y-1 text-[13.5px]" style={{ color: 'var(--yb-ink)' }}>
                        {pendingPlaceholders.placeholders.map((ph) => (
                          <li key={ph} data-testid="placeholder-item" className="font-mono">• {ph}</li>
                        ))}
                      </ul>
                    )}
                    <button
                      type="button"
                      onClick={() => setPendingPlaceholders(null)}
                      data-testid="placeholders-dismiss"
                      className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border bg-white text-[12.5px]"
                      style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)' }}
                    >
                      Got it
                    </button>
                  </div>
                )}

                {project.is_public && project.generated_html && (
                  <div className="mt-4 p-4 rounded-xl border flex flex-col md:flex-row md:items-center justify-between gap-3" style={{ borderColor: 'var(--yb-border)', backgroundColor: '#EEF2FF' }}>
                    <div className="min-w-0 flex-1">
                      <p className="text-[12px] uppercase tracking-wider mb-1" style={{ color: 'var(--yb-indigo)' }}>Public share link</p>
                      <p data-testid="share-url" className="text-[13.5px] font-mono truncate" style={{ color: 'var(--yb-ink)' }}>{shareUrl}</p>
                      <p className="text-[12.5px] mt-1" style={{ color: 'var(--yb-muted)' }}>
                        Anyone with this link can view your page. Toggle Public off to make it private again.
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={copyShareLink} data-testid="copy-share-link" className="yb-btn-primary inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[13px] font-semibold">
                        {copyStatus === 'copied' ? <Check size={14} /> : <Copy size={14} />}
                        {copyStatus === 'copied' ? 'Copied' : 'Copy link'}
                      </button>
                      <a href={shareUrl} target="_blank" rel="noreferrer" data-testid="open-share-link" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border text-[13px] bg-white" style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)' }}>
                        Open
                      </a>
                    </div>
                  </div>
                )}

                {/* Business-name editor — instant, free, LLM-free fix
                    for freeform-prompt projects whose "business name"
                    is actually a prompt fragment. Server updates the
                    source of truth and re-injects title / meta / og /
                    JSON-LD at serve time; body content is best-effort
                    swapped in the stored HTML. */}
                {project.generated_html && (
                  <div data-testid="biz-name-editor" className="mt-3 p-4 rounded-xl border" style={{ borderColor: 'var(--yb-border)', backgroundColor: 'white' }}>
                    <p className="text-[12px] uppercase tracking-wider mb-0.5" style={{ color: 'var(--yb-muted)' }}>Business name</p>
                    <p className="text-[13.5px]" style={{ color: 'var(--yb-ink)' }}>
                      This shows up in your page title, Google preview, and social share cards. Fix it here — it’s instant and free.
                    </p>
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <input
                        type="text"
                        data-testid="biz-name-input"
                        value={bizDraft}
                        onChange={(e) => setBizDraft(e.target.value)}
                        placeholder="e.g. Joe’s Plumbing"
                        disabled={bizSaving}
                        maxLength={120}
                        className="flex-1 min-w-[220px] px-3 py-2 text-[13.5px] rounded-full border bg-white outline-none disabled:opacity-60"
                        style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-ink)' }}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveBizName(); }}
                      />
                      <button
                        type="button"
                        onClick={saveBizName}
                        disabled={bizSaving || !bizDraft.trim()}
                        data-testid="biz-name-save"
                        className="yb-btn-primary inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold disabled:opacity-60"
                      >
                        {bizStatus === 'saved' ? <Check size={14} /> : null}
                        {bizSaving ? 'Saving…' : (bizStatus === 'saved' ? 'Saved' : 'Save')}
                      </button>
                    </div>
                    {bizError && (
                      <p data-testid="biz-name-error" className="mt-2 text-[12.5px]" style={{ color: '#B4530A' }}>{bizError}</p>
                    )}
                    {pendingReseed && (
                      <div data-testid="biz-name-reseed-prompt" className="mt-3 p-3 rounded-lg border flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: '#DDD6FE', backgroundColor: '#F5F3FF' }}>
                        <p className="text-[13px]" style={{ color: 'var(--yb-ink)' }}>
                          Your custom link still points at the old name. Update it to match?
                        </p>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={() => setPendingReseed(null)}
                            disabled={reseeding}
                            data-testid="biz-name-reseed-skip"
                            className="px-3 py-1.5 rounded-full border text-[12.5px] bg-white disabled:opacity-60"
                            style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-muted)' }}
                          >
                            Keep old link
                          </button>
                          <button
                            type="button"
                            onClick={reseedSlug}
                            disabled={reseeding}
                            data-testid="biz-name-reseed-confirm"
                            className="yb-btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-semibold disabled:opacity-60"
                          >
                            {reseeding ? 'Updating…' : 'Update link'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Custom slug editor — lets owners pick a friendly slug
                    like "joes-plumbing" so the share URL becomes
                    /api/public/projects/by-slug/joes-plumbing/index.html
                    instead of the raw UUID. Reserved words + collisions
                    are enforced server-side. */}
                {project.is_public && project.generated_html && (
                  <div data-testid="slug-editor" className="mt-3 p-4 rounded-xl border" style={{ borderColor: 'var(--yb-border)', backgroundColor: 'white' }}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <p className="text-[12px] uppercase tracking-wider mb-0.5" style={{ color: 'var(--yb-muted)' }}>Custom link</p>
                        <p className="text-[13.5px]" style={{ color: 'var(--yb-ink)' }}>
                          {slug ? (
                            <>
                              Your slug is <strong data-testid="current-slug" className="font-mono" style={{ color: 'var(--yb-indigo)' }}>{slug}</strong> —
                              tradies see a nicer URL when you share.
                            </>
                          ) : (
                            <>Give your page a friendlier URL — e.g. <span className="font-mono">joes-plumbing</span> or <span className="font-mono">brisbane-sparkies</span>.</>
                          )}
                        </p>
                      </div>
                      {slug && (
                        <button
                          type="button"
                          onClick={clearSlug}
                          disabled={slugSaving}
                          data-testid="slug-clear"
                          className="text-[12px] px-2.5 py-1 rounded-full border bg-white disabled:opacity-60"
                          style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-muted)' }}
                        >
                          Reset to default
                        </button>
                      )}
                    </div>
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <div className="flex items-center gap-0 flex-1 min-w-[220px] rounded-full border overflow-hidden bg-white" style={{ borderColor: 'var(--yb-border)' }}>
                        <span className="pl-3 pr-1 text-[13px] font-mono select-none" style={{ color: 'var(--yb-muted)' }}>
                          /api/public/
                        </span>
                        <input
                          type="text"
                          data-testid="slug-input"
                          value={slugDraft}
                          onChange={(e) => setSlugDraft(e.target.value)}
                          placeholder={slug || 'joes-plumbing'}
                          disabled={slugSaving}
                          maxLength={40}
                          autoCapitalize="off"
                          autoCorrect="off"
                          spellCheck={false}
                          className="flex-1 py-2 pr-3 text-[13.5px] font-mono outline-none disabled:opacity-60"
                          style={{ color: 'var(--yb-ink)', backgroundColor: 'transparent' }}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveSlug(); }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={saveSlug}
                        disabled={slugSaving || !slugDraft.trim()}
                        data-testid="slug-save"
                        className="yb-btn-primary inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold disabled:opacity-60"
                      >
                        {slugStatus === 'saved' ? <Check size={14} /> : null}
                        {slugSaving ? 'Saving…' : (slugStatus === 'saved' ? 'Saved' : (slug ? 'Update' : 'Save'))}
                      </button>
                    </div>
                    {slugError && (
                      <p data-testid="slug-error" className="mt-2 text-[12.5px]" style={{ color: '#B4530A' }}>{slugError}</p>
                    )}
                    <p className="mt-2 text-[11.5px]" style={{ color: 'var(--yb-muted)' }}>
                      3–40 characters, lowercase letters, numbers, and single dashes. Some words are reserved (login, admin, blog, etc.).
                    </p>
                  </div>
                )}
                {!project.is_public && project.generated_html && (
                  <div className="mt-4 p-3 rounded-xl border inline-flex items-center gap-2 text-[13px]" style={{ borderColor: 'var(--yb-border)', backgroundColor: 'rgba(255,255,255,0.7)', color: 'var(--yb-muted)' }}>
                    <Lock size={13} style={{ color: 'var(--yb-muted)' }} />
                    This project is private. Click <strong className="mx-1" style={{ color: 'var(--yb-ink)' }}>Share</strong> above to create a public link.
                  </div>
                )}

                {confirmDelete && (
                  <div className="mt-4 p-4 rounded-xl border flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: '#F1D9C4', backgroundColor: '#FDF5EE' }}>                    <p className="text-[13.5px]" style={{ color: '#7A3B03' }}>
                      Delete <strong>{project.name}</strong>? This can’t be undone.
                    </p>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => setConfirmDelete(false)} className="px-3 py-1.5 rounded-full border text-[12.5px]" style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)' }}>
                        Cancel
                      </button>
                      <button onClick={doDelete} disabled={deleting} className="yb-btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-semibold">
                        {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Delete project
                      </button>
                    </div>
                  </div>
                )}

                {heroShuffleMsg && (
                  <div
                    data-testid="hero-shuffle-msg"
                    className="mt-3 text-[13px] rounded-lg border px-3 py-2"
                    style={{ borderColor: 'var(--yb-border)', backgroundColor: '#F5F4F0', color: 'var(--yb-text)' }}
                  >
                    {heroShuffleMsg}
                  </div>
                )}

                {/* Site stats — small privacy-friendly dashboard. Shown once
                    the page has actually been generated (before that, there's
                    obviously nothing to count). */}
                {project.generated_html && (
                  <StatsPanel projectId={id} getToken={getToken} />
                )}

                {/* Get found on Google — plain-English SEO checklist. Ticks
                    persist in localStorage. Only meaningful once the page is
                    live. */}
                {project.generated_html && project.is_public && (
                  <GetFoundOnGoogle projectId={id} />
                )}

                <div className="mt-6 bg-white rounded-2xl border p-5" style={{ borderColor: 'var(--yb-border)' }}>
                  <p className="text-[11px] uppercase tracking-wider mb-1.5" style={{ color: 'var(--yb-muted)' }}>Your prompt</p>
                  {!editing ? (
                    <p className="text-[15px] leading-relaxed" style={{ color: 'var(--yb-text)' }}>{project.prompt}</p>
                  ) : editMode === 'wizard' && savedIntake && savedIntake.trade && savedIntake.town ? (
                    <div data-testid="prompt-editor-wizard">
                      <GuidedIntake
                        initial={savedIntake}
                        busy={wizardBusy}
                        submitLabel="Save & regenerate"
                        onSubmit={handleWizardRegenerate}
                        onAdvanced={() => setEditMode('raw')}
                      />
                      <div className="mt-3 flex items-center">
                        <button
                          type="button"
                          onClick={() => setEditing(false)}
                          data-testid="prompt-editor-cancel"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[13px]"
                          style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)' }}
                        >
                          <X size={13} /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div data-testid="prompt-editor-raw">
                      {savedIntake && savedIntake.trade && savedIntake.town && (
                        <p className="text-[12.5px] mb-2" style={{ color: 'var(--yb-muted)' }}>
                          Editing the raw prompt.
                          <button
                            type="button"
                            onClick={() => setEditMode('wizard')}
                            data-testid="prompt-editor-back-to-wizard"
                            className="ml-1 underline decoration-dotted underline-offset-4"
                            style={{ color: 'var(--yb-indigo)' }}
                          >
                            ← Back to the guided wizard
                          </button>
                        </p>
                      )}
                      <textarea
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        rows={3}
                        data-testid="prompt-editor-textarea"
                        className="w-full px-3 py-2 rounded-lg border text-[14.5px] outline-none resize-y bg-white"
                        style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-ink)' }}
                      />
                      <div className="mt-3 flex items-center gap-2">
                        <button
                          onClick={savePromptEdit}
                          disabled={savingEdit || isBusy}
                          className="yb-btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-semibold disabled:opacity-70"
                        >
                          {savingEdit ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                          Save & regenerate
                        </button>
                        <button
                          onClick={() => setEditing(false)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[13px]"
                          style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)' }}
                        >
                          <X size={13} /> Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {genStatus === 'limit' && (
                  <div
                    data-testid="gen-limit-banner"
                    data-reason={genLimitReason}
                    className="mt-4 p-4 rounded-xl border flex items-start gap-2"
                    style={{ borderColor: '#C7D2FE', backgroundColor: '#EEF2FF' }}
                  >
                    <AlertCircle size={16} className="mt-0.5" style={{ color: 'var(--yb-indigo)' }} />
                    <div className="flex-1">
                      <p className="text-[14px] font-medium" style={{ color: 'var(--yb-ink)' }}>
                        {genLimitReason === 'global_daily_limit' ? 'YouBuild is at capacity today.' : "You've hit today's limit."}
                      </p>
                      <p className="text-[13px] mt-0.5" style={{ color: 'var(--yb-text)' }}>{genError}</p>
                    </div>
                  </div>
                )}

                {(genStatus === 'error' || genStatus === 'timeout') && (
                  <div className="mt-4 p-4 rounded-xl border flex items-start gap-2" style={{ borderColor: '#F1D9C4', backgroundColor: '#FDF5EE' }}>
                    <AlertCircle size={16} className="mt-0.5" style={{ color: '#B4530A' }} />
                    <div className="flex-1">
                      <p className="text-[14px] font-medium" style={{ color: '#7A3B03' }}>
                        {genStatus === 'timeout' ? 'This is taking longer than expected.' : 'Generation didn’t finish.'}
                      </p>
                      {genError && (
                        <p className="text-[13px] mt-0.5" style={{ color: '#7A3B03' }}>{genError}</p>
                      )}
                      <button onClick={runGenerate} className="mt-2 yb-btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-semibold">
                        <RefreshCw size={13} /> Retry
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {loadState === 'ready' && project && (
          <section className="pb-24 lg:pb-32">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
              {/* Add your photos — optional. If empty, generation uses Unsplash. */}
              <div
                className="rounded-2xl border bg-white p-5 mb-6"
                style={{ borderColor: 'var(--yb-border)' }}
                data-testid="photos-card"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                      <ImagePlus size={16} />
                    </div>
                    <div>
                      <h2 className="font-serif text-2xl leading-tight" style={{ color: 'var(--yb-ink)' }}>
                        Add your photos
                      </h2>
                      <p className="text-[12.5px]" style={{ color: 'var(--yb-muted)' }}>
                        Upload photos of your shop, team, or past jobs. Your next build will use them instead of stock images.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className="text-[12px] font-medium px-2.5 py-1 rounded-full"
                      style={{ backgroundColor: '#EEF2FF', color: 'var(--yb-indigo)' }}
                      data-testid="photos-count"
                    >
                      {photos.length}/8
                    </span>
                    <input
                      ref={photoInputRef}
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => uploadPhotos(e.target.files)}
                      disabled={uploading || photos.length >= 8}
                      className="hidden"
                      data-testid="photos-input"
                    />
                    <button
                      type="button"
                      onClick={() => photoInputRef.current?.click()}
                      disabled={uploading || photos.length >= 8}
                      data-testid="photos-upload-btn"
                      className="yb-btn-primary inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold disabled:opacity-60"
                    >
                      {uploading ? (
                        <>
                          <Loader2 size={13} className="animate-spin" /> Uploading…
                        </>
                      ) : (
                        <>
                          <Upload size={13} /> Add photos
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {photosError && (
                  <div
                    className="mt-3 text-[12.5px] rounded-lg border px-3 py-2"
                    style={{ borderColor: '#FCA5A5', backgroundColor: '#FEF2F2', color: '#991B1B' }}
                    data-testid="photos-error"
                  >
                    {typeof photosError === 'string' ? photosError : 'Could not process photos.'}
                  </div>
                )}

                {photos.length === 0 && !photosLoading ? (
                  <div className="mt-4 rounded-xl border-2 border-dashed p-6 text-center" style={{ borderColor: 'var(--yb-border)' }}>
                    <p className="text-[13.5px]" style={{ color: 'var(--yb-ink)' }}>
                      No photos yet — this build will use stock imagery.
                    </p>
                    <p className="mt-1 text-[12.5px]" style={{ color: 'var(--yb-muted)' }}>
                      JPG, PNG, or WebP. Up to 5MB each, 8 photos total.
                    </p>
                  </div>
                ) : (
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="photos-grid">
                    {photos.map((p) => (
                      <div
                        key={p.id}
                        className="relative rounded-xl overflow-hidden border bg-[#FAF9F5] aspect-[4/3] group"
                        style={{ borderColor: 'var(--yb-border)' }}
                        data-testid={`photo-tile-${p.id}`}
                      >
                        <img
                          src={p.public_url}
                          alt={p.alt || 'Owner photo'}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(p.id)}
                          data-testid={`photo-remove-${p.id}`}
                          aria-label="Remove photo"
                          className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/95 border shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-600"
                          style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-muted)' }}
                        >
                          <X size={13} />
                        </button>
                        {p.alt && (
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 text-[11px] text-white truncate">
                            {p.alt}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {photos.length > 0 && (
                  <p className="mt-3 text-[12px]" style={{ color: 'var(--yb-muted)' }}>
                    Your photos will replace stock images on the next <strong>Generate</strong>. First photo = hero.
                  </p>
                )}
              </div>

              {/* Add a review — owner-typed testimonials */}
              <div
                className="rounded-2xl border bg-white p-5 mb-6"
                style={{ borderColor: 'var(--yb-border)' }}
                data-testid="reviews-card"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#FEF3C7', color: '#B4530A' }}>
                      <Star size={16} />
                    </div>
                    <div>
                      <h2 className="font-serif text-2xl leading-tight" style={{ color: 'var(--yb-ink)' }}>
                        Reviews
                      </h2>
                      <p className="text-[12.5px]" style={{ color: 'var(--yb-muted)' }}>
                        Add real testimonials from your customers. We never write these for you.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={openNewReview}
                    data-testid="reviews-add-btn"
                    className="yb-btn-primary inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold"
                  >
                    <Plus size={13} /> Add a review
                  </button>
                </div>

                {reviewsError && (
                  <div
                    className="mt-3 text-[12.5px] rounded-lg border px-3 py-2"
                    style={{ borderColor: '#FCA5A5', backgroundColor: '#FEF2F2', color: '#991B1B' }}
                    data-testid="reviews-error"
                  >
                    {typeof reviewsError === 'string' ? reviewsError : 'Could not process reviews.'}
                  </div>
                )}

                {reviews.length === 0 ? (
                  <div
                    className="mt-4 rounded-xl border-2 border-dashed p-6 text-center"
                    style={{ borderColor: 'var(--yb-border)' }}
                    data-testid="reviews-empty-hint"
                  >
                    <p className="text-[13.5px]" style={{ color: 'var(--yb-ink)' }}>
                      No reviews yet.
                    </p>
                    <p className="mt-1 text-[12.5px]" style={{ color: 'var(--yb-muted)' }}>
                      Add reviews to build trust. Publishing works either way, but real reviews make a big difference.
                    </p>
                  </div>
                ) : (
                  <div className="mt-4 space-y-3" data-testid="reviews-list">
                    {reviews.map((r) => (
                      <div
                        key={r.id}
                        className="rounded-xl border bg-[#FAF9F5] p-4"
                        style={{ borderColor: 'var(--yb-border)' }}
                        data-testid={`review-row-${r.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            {r.rating ? (
                              <div className="flex items-center gap-0.5 mb-1" aria-label={`${r.rating} out of 5`}>
                                {[1, 2, 3, 4, 5].map((n) => (
                                  <Star key={n} size={13}
                                        style={{ color: n <= (r.rating || 0) ? '#F59E0B' : '#E5E7EB',
                                                 fill: n <= (r.rating || 0) ? '#F59E0B' : 'transparent' }} />
                                ))}
                              </div>
                            ) : null}
                            <p className="text-[14px]" style={{ color: 'var(--yb-ink)' }}>{r.review_text}</p>
                            <p className="mt-1.5 text-[12.5px] font-medium" style={{ color: 'var(--yb-muted)' }}>
                              — {r.first_name}{r.suburb ? `, ${r.suburb}` : ''}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => openEditReview(r)}
                              data-testid={`review-edit-${r.id}`}
                              className="p-2 rounded-full border bg-white"
                              style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-muted)' }}
                              aria-label="Edit review"
                            >
                              <Edit3 size={13} />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeReview(r.id)}
                              data-testid={`review-delete-${r.id}`}
                              className="p-2 rounded-full border bg-white hover:text-red-600"
                              style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-muted)' }}
                              aria-label="Delete review"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Review editor modal */}
              {reviewDraft && (
                <div
                  className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center p-4"
                  style={{ backgroundColor: 'rgba(15,23,42,0.5)' }}
                  data-testid="review-editor-backdrop"
                  onClick={(e) => { if (e.target === e.currentTarget) setReviewDraft(null); }}
                >
                  <div
                    className="w-full sm:w-[480px] bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
                    data-testid="review-editor"
                  >
                    <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between" style={{ borderColor: 'var(--yb-border)' }}>
                      <h3 className="font-serif text-xl leading-tight" style={{ color: 'var(--yb-ink)' }}>
                        {reviewDraft.id ? 'Edit review' : 'Add a review'}
                      </h3>
                      <button
                        type="button"
                        onClick={() => setReviewDraft(null)}
                        className="p-2 rounded-full hover:bg-neutral-100"
                        style={{ color: 'var(--yb-muted)' }}
                        aria-label="Close"
                        data-testid="review-editor-close"
                      >
                        <X size={16} />
                      </button>
                    </div>

                    <div className="p-5 space-y-4">
                      <div>
                        <label htmlFor="rev-text" className="block text-[13px] font-medium mb-1.5" style={{ color: 'var(--yb-ink)' }}>
                          Review text <span style={{ color: '#DC2626' }}>*</span>
                        </label>
                        <textarea
                          id="rev-text"
                          rows={4}
                          value={reviewDraft.review_text}
                          onChange={(e) => setReviewDraft((d) => ({ ...d, review_text: e.target.value }))}
                          placeholder="What did the customer say?"
                          data-testid="review-input-text"
                          className="w-full px-3 py-2 rounded-lg border text-[14px] outline-none focus:border-[var(--yb-indigo)] resize-y"
                          style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-ink)' }}
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label htmlFor="rev-name" className="block text-[13px] font-medium mb-1.5" style={{ color: 'var(--yb-ink)' }}>
                            First name <span style={{ color: '#DC2626' }}>*</span>
                          </label>
                          <input
                            id="rev-name"
                            type="text"
                            value={reviewDraft.first_name}
                            onChange={(e) => setReviewDraft((d) => ({ ...d, first_name: e.target.value }))}
                            placeholder="Robin"
                            data-testid="review-input-name"
                            className="w-full px-3 py-2 rounded-lg border text-[14px] outline-none focus:border-[var(--yb-indigo)]"
                            style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-ink)' }}
                          />
                        </div>
                        <div>
                          <label htmlFor="rev-suburb" className="block text-[13px] font-medium mb-1.5" style={{ color: 'var(--yb-ink)' }}>
                            Suburb (optional)
                          </label>
                          <input
                            id="rev-suburb"
                            type="text"
                            value={reviewDraft.suburb}
                            onChange={(e) => setReviewDraft((d) => ({ ...d, suburb: e.target.value }))}
                            placeholder="Bondi"
                            data-testid="review-input-suburb"
                            className="w-full px-3 py-2 rounded-lg border text-[14px] outline-none focus:border-[var(--yb-indigo)]"
                            style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-ink)' }}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[13px] font-medium mb-1.5" style={{ color: 'var(--yb-ink)' }}>
                          Star rating (optional)
                        </label>
                        <div className="flex items-center gap-1" data-testid="review-input-rating">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setReviewDraft((d) => ({ ...d, rating: d.rating === n ? 0 : n }))}
                              data-testid={`review-star-${n}`}
                              aria-label={`${n} stars`}
                              className="p-1"
                            >
                              <Star size={22}
                                    style={{
                                      color: n <= (reviewDraft.rating || 0) ? '#F59E0B' : '#D1D5DB',
                                      fill: n <= (reviewDraft.rating || 0) ? '#F59E0B' : 'transparent',
                                    }} />
                            </button>
                          ))}
                          {reviewDraft.rating ? (
                            <button
                              type="button"
                              onClick={() => setReviewDraft((d) => ({ ...d, rating: 0 }))}
                              className="ml-2 text-[12px] underline"
                              style={{ color: 'var(--yb-muted)' }}
                            >
                              clear
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div className="sticky bottom-0 bg-white border-t px-5 py-4 flex items-center gap-2" style={{ borderColor: 'var(--yb-border)' }}>
                      <button
                        type="button"
                        onClick={() => setReviewDraft(null)}
                        className="px-4 py-2 rounded-full border text-[13px] bg-white"
                        style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)' }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={saveReview}
                        disabled={reviewSaving}
                        data-testid="review-editor-save"
                        className="yb-btn-primary flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-[13.5px] font-semibold disabled:opacity-70"
                      >
                        {reviewSaving ? (<><Loader2 size={13} className="animate-spin" /> Saving…</>) : (reviewDraft.id ? 'Save changes' : 'Add review')}
                      </button>
                    </div>
                  </div>
                </div>
              )}



              <div className="rounded-2xl border overflow-hidden bg-white" style={{ borderColor: 'var(--yb-border)' }}>
                <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'var(--yb-border)', backgroundColor: '#FAF9F5' }}>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400/60" />
                  </div>
                  <div className="ml-3 text-[12px] font-mono truncate" style={{ color: 'var(--yb-muted)' }}>
                    preview • {project.name}
                  </div>
                  <div className="ml-auto text-[11.5px]" style={{ color: 'var(--yb-muted)' }}>
                    Last built: {formatDate(project.updated_at)}
                  </div>
                </div>

                <div className="relative" style={{ minHeight: 620 }}>
                  {isBusy && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/85 backdrop-blur-sm">
                      <Loader2 size={26} className="animate-spin" style={{ color: 'var(--yb-indigo)' }} />
                      <p className="font-serif text-2xl" style={{ color: 'var(--yb-ink)' }}>
                        Building your page<span className="italic">…</span>
                      </p>
                      <p className="text-[13px] max-w-md text-center" style={{ color: 'var(--yb-muted)' }}>
                        This can take up to ~2 minutes for a full, polished page.
                        You can leave this open — we’ll finish in the background.
                      </p>
                    </div>
                  )}

                  {project.generated_html ? (
                    <iframe
                      title="Generated preview"
                      srcDoc={project.generated_html}
                      sandbox="allow-scripts"
                      className="w-full block"
                      style={{ height: '78vh', border: 0, backgroundColor: '#fff' }}
                    />
                  ) : (
                    !isBusy && (
                      <div className="flex flex-col items-center justify-center py-24 text-center px-6">
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: '#EEF2FF', color: 'var(--yb-indigo)' }}>
                          <Wand2 size={22} />
                        </div>
                        <h2 className="font-serif text-3xl" style={{ color: 'var(--yb-ink)' }}>
                          Ready when you are.
                        </h2>
                        <p className="mt-2 max-w-md text-[14.5px]" style={{ color: 'var(--yb-muted)' }}>
                          Click <strong>Generate</strong> and YouBuild will build a real page from your prompt.
                          Takes about half a minute.
                        </p>
                        <button
                          onClick={runGenerate}
                          className="yb-btn-primary inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13.5px] font-semibold mt-6"
                        >
                          <Wand2 size={14} /> Generate this page
                        </button>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>


            {/* Soft-confirm modal — appears when Publishing with placeholders unfilled */}
            {confirmPublish && (
              <div
                className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center p-4"
                style={{ backgroundColor: 'rgba(15,23,42,0.5)' }}
                data-testid="publish-confirm-backdrop"
                onClick={(e) => { if (e.target === e.currentTarget) setConfirmPublish(false); }}
              >
                <div
                  className="w-full sm:w-[480px] bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
                  data-testid="publish-confirm-modal"
                >
                  <div className="p-6">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: '#FFFBEB', color: '#B4530A' }}>
                        <ListChecks size={18} />
                      </div>
                      <div>
                        <h3 className="font-serif text-xl leading-tight" style={{ color: 'var(--yb-ink)' }}>
                          {placeholderCount === 1
                            ? 'You have 1 field unfilled.'
                            : `You have ${placeholderCount} fields unfilled.`}
                        </h3>
                        <p className="mt-1.5 text-[13.5px]" style={{ color: 'var(--yb-muted)' }}>
                          {placeholderCount === 1
                            ? "It'll be hidden from your public page. Add it now, or publish anyway?"
                            : "They'll be hidden from your public page. Add them now, or publish anyway?"}
                        </p>
                        {placeholders.length > 0 && (
                          <ul className="mt-3 flex flex-wrap gap-1.5" data-testid="publish-confirm-list">
                            {placeholders.map((ph) => (
                              <li
                                key={ph}
                                className="text-[12px] px-2.5 py-1 rounded-full border font-mono"
                                style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)', backgroundColor: '#FAF9F5' }}
                              >
                                {ph.replace(/^\[Add\s+your\s+/i, '').replace(/\]$/, '')}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="px-6 pb-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <button
                      type="button"
                      onClick={() => { setConfirmPublish(false); setFillOpen(true); }}
                      data-testid="publish-confirm-fill"
                      className="yb-btn-primary inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-[13.5px] font-semibold"
                    >
                      Add now
                    </button>
                    <button
                      type="button"
                      onClick={() => { setConfirmPublish(false); togglePublic({ skipPlaceholderConfirm: true }); }}
                      data-testid="publish-confirm-anyway"
                      className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full border text-[13.5px] bg-white"
                      style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)' }}
                    >
                      Publish anyway
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmPublish(false)}
                      className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-[13px]"
                      style={{ color: 'var(--yb-muted)' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}


            {/* Fill missing fields — side panel (mobile: full-width sheet, desktop: right slide-over) */}
            {fillOpen && (
              <div
                className="fixed inset-0 z-50 flex items-end sm:items-stretch sm:justify-end"
                style={{ backgroundColor: 'rgba(15,23,42,0.5)' }}
                data-testid="fill-panel-backdrop"
                onClick={(e) => { if (e.target === e.currentTarget) setFillOpen(false); }}
              >
                <div
                  className="w-full sm:w-[420px] bg-white shadow-2xl max-h-[90vh] sm:max-h-full sm:h-full overflow-y-auto rounded-t-2xl sm:rounded-none flex flex-col"
                  data-testid="fill-panel"
                >
                  <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between" style={{ borderColor: 'var(--yb-border)' }}>
                    <div>
                      <h3 className="font-serif text-xl leading-tight" style={{ color: 'var(--yb-ink)' }}>
                        Fill missing fields
                      </h3>
                      <p className="text-[12.5px] mt-0.5" style={{ color: 'var(--yb-muted)' }}>
                        Each value replaces its placeholder on the page. No regenerate needed.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFillOpen(false)}
                      className="p-2 rounded-full hover:bg-neutral-100"
                      style={{ color: 'var(--yb-muted)' }}
                      aria-label="Close"
                      data-testid="fill-panel-close"
                    >
                      <X size={16} />
                    </button>
                  </div>

                  <div className="p-5 flex-1">
                    {placeholderCount === 0 ? (
                      <div className="text-center py-10">
                        <div className="inline-flex w-12 h-12 rounded-full items-center justify-center mb-3" style={{ backgroundColor: '#DCFCE7', color: '#166534' }}>
                          <Check size={20} />
                        </div>
                        <p className="text-[14.5px] font-medium" style={{ color: 'var(--yb-ink)' }}>All filled in.</p>
                        <p className="mt-1 text-[13px]" style={{ color: 'var(--yb-muted)' }}>You're ready to publish.</p>
                      </div>
                    ) : (
                      <div className="space-y-4" data-testid="fill-fields-list">
                        {placeholders.map((ph) => {
                          const label = ph.replace(/^\[Add\s+your\s+/i, '').replace(/\]$/, '').trim();
                          const inputId = `fill-${ph.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
                          return (
                            <div key={ph} data-testid={`fill-field-${ph}`}>
                              <label htmlFor={inputId} className="block text-[13px] font-medium mb-1.5" style={{ color: 'var(--yb-ink)' }}>
                                {label.charAt(0).toUpperCase() + label.slice(1)}
                              </label>
                              <input
                                id={inputId}
                                type="text"
                                value={fillValues[ph] || ''}
                                onChange={(e) => setFillValues((prev) => ({ ...prev, [ph]: e.target.value }))}
                                placeholder={ph}
                                data-testid={`fill-input-${ph}`}
                                className="w-full px-3 py-2 rounded-lg border text-[14px] outline-none focus:border-[var(--yb-indigo)]"
                                style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-ink)' }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {fillError && (
                      <div
                        className="mt-4 text-[13px] rounded-lg border px-3 py-2"
                        style={{ borderColor: '#FCA5A5', backgroundColor: '#FEF2F2', color: '#991B1B' }}
                        data-testid="fill-panel-error"
                      >
                        {typeof fillError === 'string' ? fillError : 'Could not save.'}
                      </div>
                    )}
                  </div>

                  {placeholderCount > 0 && (
                    <div className="sticky bottom-0 bg-white border-t px-5 py-4 flex items-center gap-2" style={{ borderColor: 'var(--yb-border)' }}>
                      <button
                        type="button"
                        onClick={() => setFillOpen(false)}
                        className="px-4 py-2 rounded-full border text-[13px] bg-white"
                        style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)' }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={saveFillValues}
                        disabled={fillSaving}
                        data-testid="fill-panel-save"
                        className="yb-btn-primary flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-[13.5px] font-semibold disabled:opacity-70"
                      >
                        {fillSaving ? (<><Loader2 size={13} className="animate-spin" /> Saving…</>) : 'Save filled fields'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}


            {/* Leads / Enquiries — Part 3 */}
            <div className="mt-10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#EEF2FF', color: 'var(--yb-indigo)' }}>
                    <Inbox size={16} />
                  </div>
                  <div>
                    <h2 className="font-serif text-2xl leading-tight" style={{ color: 'var(--yb-ink)' }}>
                      Leads
                    </h2>
                    <p className="text-[12.5px]" style={{ color: 'var(--yb-muted)' }}>
                      Every quote request from your page lands here, newest first.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={loadLeads}
                  disabled={leadsLoading}
                  data-testid="leads-refresh-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border bg-white text-[12.5px] transition-colors hover:border-[var(--yb-indigo)] hover:text-[var(--yb-indigo)]"
                  style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)' }}
                >
                  <RefreshCw size={12} className={leadsLoading ? 'animate-spin' : ''} /> Refresh
                </button>
              </div>

              {leadsError && (
                <div className="rounded-xl border p-3 mb-3 text-[13px]" style={{ borderColor: '#FCA5A5', backgroundColor: '#FEF2F2', color: '#991B1B' }}>
                  {typeof leadsError === 'string' ? leadsError : 'Could not load leads.'}
                </div>
              )}

              {leads.length === 0 && !leadsLoading ? (
                <div
                  data-testid="leads-empty-state"
                  className="rounded-2xl border bg-white p-8 text-center"
                  style={{ borderColor: 'var(--yb-border)' }}
                >
                  <p className="text-[14.5px]" style={{ color: 'var(--yb-ink)' }}>
                    No enquiries yet.
                  </p>
                  <p className="mt-1.5 text-[13px]" style={{ color: 'var(--yb-muted)' }}>
                    Publish your page and share the link. When someone fills in the quote form, their details show up here.
                  </p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="leads-list">
                  {leads.map((lead) => (
                    <div
                      key={lead.id}
                      className="rounded-xl border bg-white p-4"
                      style={{ borderColor: 'var(--yb-border)' }}
                      data-testid={`lead-row-${lead.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-[14.5px]" style={{ color: 'var(--yb-ink)' }}>
                            {lead.name || 'Unknown'}
                          </p>
                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px]" style={{ color: 'var(--yb-muted)' }}>
                            {lead.phone && (
                              <a
                                href={`tel:${lead.phone.replace(/\s+/g, '')}`}
                                className="inline-flex items-center gap-1 hover:text-[var(--yb-indigo)]"
                              >
                                <Phone size={11} /> {lead.phone}
                              </a>
                            )}
                            {lead.email && (
                              <a
                                href={`mailto:${lead.email}`}
                                className="inline-flex items-center gap-1 hover:text-[var(--yb-indigo)]"
                              >
                                <Mail size={11} /> {lead.email}
                              </a>
                            )}
                            {lead.suburb && (
                              <span className="inline-flex items-center gap-1">
                                <MapPin size={11} /> {lead.suburb}
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="text-[11.5px] whitespace-nowrap" style={{ color: 'var(--yb-muted)' }}>
                          {formatDate(lead.created_at)}
                        </span>
                      </div>
                      {lead.message && (
                        <p className="mt-3 text-[13.5px] whitespace-pre-wrap" style={{ color: 'var(--yb-text)' }}>
                          {lead.message}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ProjectDetail;
